import { useMemo } from 'react';

export type PlatformType = 'telegram' | 'max' | 'unknown';

export interface PlatformUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
}

export interface PlatformAPI {
  /** Resolved platform - 'unknown' when running outside any messenger (dev/browser). */
  platform: PlatformType;

  /** Raw initData string. Send to backend in Authorization header. */
  initData: string;

  /** Current authenticated user from the platform SDK. */
  user: PlatformUser | undefined;

  /** Notify the SDK that the app is ready (no-op on platforms that don't expose it). */
  ready: () => void;

  /**
   * Show the native back button in the messenger header.
   * Telegram and MAX both expose a BackButton with identical API.
   */
  showBackButton: (onBack: () => void) => void;

  /** Hide the native back button. */
  hideBackButton: () => void;

  /** Haptic feedback for success/error events (no-op on platforms that don't support it). */
  notifySuccess: () => void;
  notifyError: () => void;

  /**
   * Request the user's phone number.
   * Available on MAX (Promise-based). Undefined on Telegram in this codebase.
   */
  requestContact?: () => Promise<{ phone: string } | null>;

  /** Raw SDK reference. Use sparingly - prefer the normalized methods above. */
  _raw: typeof window.Telegram.WebApp | typeof window.WebApp | null;
}

function detectPlatform(): PlatformType {
  if (typeof window === 'undefined') return 'unknown';
  if (window.Telegram?.WebApp?.initData) return 'telegram';
  if (window.WebApp?.initData) return 'max';
  return 'unknown';
}

/**
 * Unified abstraction over Telegram WebApp and MAX Mini App SDKs.
 * Detects the host platform from initData presence and normalizes the APIs
 * that overlap (BackButton, HapticFeedback). Methods absent on a platform
 * are silent no-ops.
 */
export const usePlatform = (): PlatformAPI => {
  // detectPlatform() reads window globals once on first hook call.
  // Platform doesn't change during the session, so memo with empty deps is safe.
  const platform = useMemo(() => detectPlatform(), []);

  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
  const max = typeof window !== 'undefined' ? window.WebApp : undefined;

  return useMemo<PlatformAPI>(() => {
    const initData = tg?.initData || max?.initData || '';
    const rawUser = tg?.initDataUnsafe?.user || max?.initDataUnsafe?.user;

    const ready = () => {
      if (platform === 'telegram') {
        tg?.ready();
      }
      // MAX SDK does not document a ready() method - intentional no-op.
    };

    const showBackButton = (onBack: () => void) => {
      if (platform === 'telegram' && tg?.BackButton) {
        tg.BackButton.show();
        tg.BackButton.onClick(onBack);
      } else if (platform === 'max' && max?.BackButton) {
        max.BackButton.show();
        max.BackButton.onClick(onBack);
      }
    };

    const hideBackButton = () => {
      if (platform === 'telegram' && tg?.BackButton) {
        tg.BackButton.hide();
      } else if (platform === 'max' && max?.BackButton) {
        max.BackButton.hide();
      }
    };

    const notifySuccess = () => {
      if (platform === 'telegram' && tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success');
      } else if (platform === 'max' && max?.HapticFeedback) {
        // MAX returns a Promise - we don't await, fire and forget.
        void max.HapticFeedback.notificationOccurred('success');
      }
    };

    const notifyError = () => {
      if (platform === 'telegram' && tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      } else if (platform === 'max' && max?.HapticFeedback) {
        void max.HapticFeedback.notificationOccurred('error');
      }
    };

    const requestContact =
      platform === 'max' && max?.requestContact
        ? () => max.requestContact!().catch(() => null)
        : undefined;

    return {
      platform,
      initData,
      user: rawUser,
      ready,
      showBackButton,
      hideBackButton,
      notifySuccess,
      notifyError,
      requestContact,
      _raw: platform === 'telegram' ? (tg ?? null) : (max ?? null),
    };
  }, [platform, tg, max]);
};
